/* eslint-disable react/jsx-no-bind */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close, DeleteOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import colors from '../colors.styles';
import adminFormStyles from '../shared/styles/adminForm.styles';
import { formatDraftSubtitle } from './Tasks.service';

const styles = {
  paper: {
    border: '1px solid #dbe2e6',
    borderRadius: '12px',
    boxShadow: '0 20px 50px rgba(4, 31, 65, 0.18)',
    width: '100%',
  },
  header: {
    alignItems: 'center',
    borderBottom: '1px solid #e9ecee',
    display: 'flex',
    justifyContent: 'space-between',
    p: '20px',
  },
  title: {
    color: colors.base.bentonville_blue,
    fontSize: '20px',
    fontWeight: 800,
  },
  closeButton: {
    color: '#656d80',
  },
  list: {
    backgroundColor: '#f6f6f6',
    gap: 1.5,
    maxHeight: '70vh',
    overflow: 'auto',
    p: 2.5,
  },
  card: {
    ...adminFormStyles.section,
    alignItems: 'center',
    backgroundColor: colors.base.light,
    border: '1px solid #dbe2e6',
    display: 'flex',
    gap: 1.5,
    p: 2,
  },
  // Title and chip share one text flow so a long title wraps at a word
  // boundary instead of pushing the chip onto its own line.
  draftTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 800,
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  },
  chip: {
    backgroundColor: colors.base.very_light_gray,
    color: colors.base.dark_gray,
    fontSize: '12px',
    fontWeight: 700,
    height: 24,
    ml: 1,
    verticalAlign: 'middle',
  },
  subtitle: {
    color: '#656d80',
    fontSize: '14px',
    mt: 0.5,
  },
  editButton: {
    ...adminFormStyles.secondaryButton,
    color: colors.base.bentonville_blue,
    flexShrink: 0,
  },
  deleteButton: {
    color: '#656d80',
    flexShrink: 0,
  },
  empty: {
    backgroundColor: '#f6f6f6',
    color: '#656d80',
    fontSize: '14px',
    p: 3,
    textAlign: 'center',
  },
};

export default function DraftsModal({ drafts, isOpen, onClose }) {
  const navigate = useNavigate();

  function editDraft(taskId) {
    // fromDrafts lets the form's Back button reopen this modal.
    navigate(`/tasks/${taskId}`, { state: { fromDrafts: true } });
  }

  return (
    <Dialog
      aria-labelledby="drafts-modal-title"
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={isOpen}
      PaperProps={{ sx: styles.paper }}
    >
      <Box sx={styles.header}>
        <Typography component="h2" id="drafts-modal-title" sx={styles.title}>
          Drafts
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={styles.closeButton}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      {drafts.length === 0 ? (
        <Typography sx={styles.empty}>No drafts yet.</Typography>
      ) : (
        <Stack sx={styles.list}>
          {drafts.map((draft) => (
            <Box key={draft.task_id} sx={styles.card}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={styles.draftTitle}>
                  {draft.title || 'Untitled request'}
                  <Chip label="Draft" size="small" sx={styles.chip} />
                </Typography>
                <Typography sx={styles.subtitle}>
                  {formatDraftSubtitle(draft)}
                </Typography>
              </Box>
              <Button
                onClick={() => editDraft(draft.task_id)}
                sx={styles.editButton}
                variant="outlined"
              >
                Edit
              </Button>
              <IconButton
                aria-label={`Delete ${draft.title || 'draft'}`}
                disabled
                sx={styles.deleteButton}
              >
                <DeleteOutline />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Dialog>
  );
}

DraftsModal.propTypes = {
  drafts: PropTypes.arrayOf(
    PropTypes.shape({
      task_id: PropTypes.number.isRequired,
      title: PropTypes.string,
      due_date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
      content_requests: PropTypes.arrayOf(
        PropTypes.shape({ media_type: PropTypes.string })
      ),
    })
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
